import { useCallback, useEffect, useState } from "react";
import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { useNavigate } from "react-router-dom";

import { useUserRole } from "~/hooks/trainer/useUserRole";
import { supabase } from "~/store/initializeStore";

const WEB_PUSH_TOKEN_STORAGE_KEY = "trainer-web-push-fcm-token";

const isNotificationPermissionAvailable = () =>
  typeof window !== "undefined" && "Notification" in window && "serviceWorker" in navigator;

export const useTrainerWebPushNotifications = () => {
  const { userId, hasAccessToFitnessFeatures } = useUserRole();
  const navigate = useNavigate();
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">(() =>
    isNotificationPermissionAvailable() ? Notification.permission : "unsupported",
  );

  const registerPushToken = useCallback(async () => {
    if (!userId || !hasAccessToFitnessFeatures || !isNotificationPermissionAvailable()) return;

    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
    if (!vapidKey) return;

    try {
      const supported = await isSupported();
      if (!supported || Notification.permission !== "granted") return;

      const serviceWorkerRegistration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
      const token = await getToken(getMessaging(), {
        vapidKey,
        serviceWorkerRegistration,
      });

      if (!token || window.localStorage.getItem(WEB_PUSH_TOKEN_STORAGE_KEY) === token) return;

      const { data } = await supabase.from("user_status").select("id").eq("id", userId).maybeSingle();
      const userStatusPayload = {
        appVersion: import.meta.env.PACKAGE_VERSION,
        lastChanged: Date.now(),
        state: "online",
        updatedAt: new Date().toISOString(),
      };

      if (data) {
        await supabase.from("user_status").update(userStatusPayload).eq("id", userId);
      } else {
        await supabase.from("user_status").insert({
          id: userId,
          devices: [],
          fcmTokens: [],
          createdAt: new Date().toISOString(),
          ...userStatusPayload,
        });
      }

      // TRE-1763 — sam token dopisuje RPC, a nie ten UPDATE. Ta przeglądarka mogła wcześniej
      // służyć innemu kontu (trenerka logująca się na konto testowe klientki), a tylko funkcja
      // z SECURITY DEFINER potrafi odebrać token tamtemu wierszowi.
      const { error: tokenError } = await supabase.rpc("register_push_token", { p_token: token });
      if (tokenError) {
        throw new Error(tokenError.message);
      }

      window.localStorage.setItem(WEB_PUSH_TOKEN_STORAGE_KEY, token);
    } catch {
      window.localStorage.removeItem(WEB_PUSH_TOKEN_STORAGE_KEY);
    }
  }, [hasAccessToFitnessFeatures, userId]);

  useEffect(() => {
    void registerPushToken();
  }, [registerPushToken]);

  useEffect(() => {
    if (!isNotificationPermissionAvailable() || Notification.permission !== "granted") return;

    let active = true;
    let unsubscribe: (() => void) | undefined;

    void isSupported().then((supported) => {
      if (!supported || !active) return;

      unsubscribe = onMessage(getMessaging(), (payload) => {
        const title = payload.notification?.title ?? payload.data?.title ?? "Nowe powiadomienie";
        const body = payload.notification?.body ?? payload.data?.body ?? "Otwórz panel i sprawdź szczegóły.";

        const browserNotification = new Notification(title, {
          body,
          data: payload.data,
          icon: "/favicon.png",
        });
        browserNotification.onclick = () => {
          window.focus();
          // Push payloads carry an in-app path (e.g. /support?requestId=...) — follow it
          // so the click lands on the resource, not just the focused tab (TRE-1550).
          const url = payload.data?.url;
          if (url && url.startsWith("/")) {
            navigate(url);
          }
        };
      });
    });

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [navigate]);

  const requestPermission = useCallback(async () => {
    if (!isNotificationPermissionAvailable()) {
      setPermission("unsupported");
      return;
    }

    const nextPermission = await Notification.requestPermission();
    setPermission(nextPermission);

    if (nextPermission === "granted") {
      await registerPushToken();
    }
  }, [registerPushToken]);

  return {
    permission,
    requestPermission,
    canRequestPermission:
      permission === "default" &&
      hasAccessToFitnessFeatures &&
      Boolean(userId && import.meta.env.VITE_FIREBASE_VAPID_KEY),
  };
};
